/**
 * 游戏表格组件
 * @description 展示游戏排名列表，支持勾选、排序、分页功能
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useIntl, history } from 'umi';
import { ConfigProvider, PagingQuery, TableColumn, TableProps } from 'tea-component';
import { DBTable, Bubble } from '@home/components/TeaComponents';
import Loading from '@intelligence/components/Loading';
import GameImage from '@home/components/GameImage';
import EllipsisHover from '@home/components/EllipsisHover';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import SortByIcon from '@intelligence/v3/components/TableComponents/SortByIcon';
import { getHasRatioIcon, calcMetricColumnWidth } from '@intelligence/v3/utils/columns/utils';
import { multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import { getCountryFlag } from '@home/_config/countries';
import { getDetailGameOverviewPath } from '@intelligence/utils/topchartsUtils';
import { LANGUAGE_CN } from '@home/_config/config';
import { SortMode, SortType } from '@home/enums/common';
import { MAX_RATIO_DISPLAY_VALUE, percentFormat, formatKMBValue } from '../../utils';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getDataSourceMetricKey,
  getDataSourceMetricRatioKey,
} from '../../constants';

import type { ICasualCompetitorsDataSourceProps } from '../../types';

import './index.scss';

// 勾选功能常量
const CHECKER_TOP_COUNT = 5;

interface IPublisherTableProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  loading?: boolean;
  hasRevenue?: boolean;
  // 最后一列气泡脚标是否右对齐
  isLastColumn?: boolean;
  // 指标解释 i18n key 配置
  tipsI18nKeys?: Record<string, string>;
  sequenceColumnWidth?: number;
  // 游戏列宽度
  gameColumnWidth?: number;
  // 活跃规模占比指标名
  activeRatioMetricName?: string;
  // 活跃规模占比指标列宽
  activeRatioMetricColumnWidth?: number;
  showPublisherInfo?: boolean;
  granularity?: 'monthly' | 'daily' | string;
  // 当前周期开始日期（用于环比气泡）
  startDate?: string;
  // 当前周期结束日期（用于环比气泡）
  endDate?: string;
  genreOptionsMap?: IRecord<any[]>;
  data?: IRecord[];
  // 是否显示序号列
  showSequence?: boolean;
  // 是否显示勾选列（默认 false）
  showChecker?: boolean;
  // 指标列宽度是否为自适应
  isMetricColumnAutoWidth?: boolean;
  bordered?: TableProps['bordered'];
  // 排序状态
  sorts?: IRatioMetricSortState<IRecord>[];
  onSortsChange?: (sorts: IRatioMetricSortState<IRecord>[]) => void;
  // 分页
  pagingQuery?: PagingQuery;
  onPagingChange?: (pagingQuery: PagingQuery) => void;
  // 勾选
  checkedList?: string[];
  onCheckedListChange?: (checkedList: string[]) => void;
}

const PublisherTable: React.FC<IPublisherTableProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    hasRevenue = true,
    isLastColumn = false,
    dataSource,
    tipsI18nKeys,
    sequenceColumnWidth = 40,
    gameColumnWidth = 250,
    activeRatioMetricName,
    activeRatioMetricColumnWidth,
    showPublisherInfo = true,
    granularity = 'monthly',
    startDate,
    endDate,
    genreOptionsMap,
    data = [],
    showSequence = false,
    showChecker = false,
    isMetricColumnAutoWidth = false,
    bordered = 'all',
    sorts = [],
    onSortsChange,
    pagingQuery = { pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE },
    onPagingChange,
    checkedList = [],
    onCheckedListChange,
  } = props;

  const { locale, formatMessage } = useIntl();
  const dauMetricKey = getDataSourceMetricKey(dataSource, 'daily_avg_dau');
  const dauRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'daily_avg_dau');
  const iapMetricKey = getDataSourceMetricKey(dataSource, 'iap_revenue');
  const iapRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'iap_revenue');
  const downloadMetricKey = getDataSourceMetricKey(dataSource, 'download');
  const downloadRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'download');
  const dauShareMetricKey = getDataSourceMetricKey(dataSource, 'dau_share');
  const teaLocale = locale === LANGUAGE_CN ? 'zh' : 'en';
  const shareSort = sorts.find((item) => {
    return item?.dataSource === dataSource && item?.metric === 'dau_share';
  });
  const shareSortType = shareSort?.type ?? SortType.None;

  /**
   * 前端排序后的数据
   */
  const sortedData = useMemo(() => {
    if (isEmpty(data)) {
      return [];
    }

    if (isEmpty(sorts)) {
      return [...data].sort((a, b) => {
        const aValue = a?.[dauMetricKey] ?? 0;
        const bValue = b?.[dauMetricKey] ?? 0;
        return bValue - aValue;
      });
    }
    return multiRatioMetricSort([...data], sorts);
  }, [dauMetricKey, data, sorts]);

  /**
   * 获取游戏信息列的渲染内容
   */
  const renderGameInfo = (record: IRecord, _rowKey: string) => {
    const countryAbbre = record?.publisher_country_abbre || '';
    const countryIcon = countryAbbre ? getCountryFlag(countryAbbre) : '';

    const handleGameClick = () => {
      const gameId = record?.game_id;
      if (gameId) {
        history.push(getDetailGameOverviewPath(gameId, 'mobile'));
      }
    };

    return (
      <div className="dbh-ranking-figure dbh-ranking-figure--clickable" onClick={handleGameClick}>
        <div className="dbh-ranking-figure-content">
          <div className="dbh-ranking-figure-avatar">
            <GameImage entityType="mobile" src={record?.cover || ''} alt={record?.name || ''} />
          </div>
          <div className="dbh-ranking-figure-info">
            {/* <div className="dbh-ranking-figure-title">{record?.name || '-'}</div>*/}
            <EllipsisHover className="dbh-ranking-figure-title" text={record?.name || '-'} />
            <div className="dbh-ranking-figure-desc">
              {record?.sub_genre && (
                <span
                  className={classnames('dbh-ranking-figure-desc-tag', {
                    'is-no-divider': !showPublisherInfo || !record?.publisher,
                  })}
                >
                  {getGenreNameI18n({
                    locale,
                    genreOptionsMap: genreOptionsMap || {},
                    genreSource: dataSource,
                    genreValue: record?.sub_genre,
                  }) || record?.sub_genre}
                </span>
              )}
              {showPublisherInfo && record?.publisher && (
                <div className="dbh-ranking-figure-desc-country">
                  {countryIcon && (
                    <img
                      className="dbh-ranking-figure-desc-img"
                      src={countryIcon}
                      alt={record?.publisher_country_abbre}
                    />
                  )}
                  {/* <span className="dbh-ranking-figure-desc-text">{record?.publisher}</span>*/}
                  <EllipsisHover className="dbh-ranking-figure-desc-text" text={record?.publisher} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * 构建活跃规模占比列
   */
  const buildDauShareColumn = () => {
    const tipsI18nKey = tipsI18nKeys?.dau_share;
    const tipsContent = tipsI18nKey ? (
      <span style={{ whiteSpace: 'pre-line' }}>{formatMessage({ id: tipsI18nKey })}</span>
    ) : undefined;
    const handleShareSort = () => {
      const nextSortType = shareSortType === SortType.Desc ? SortType.Asc : SortType.Desc;

      onSortsChange?.([
        {
          dataSource,
          metric: 'dau_share',
          type: nextSortType,
          mode: SortMode.Number,
        },
      ]);
    };
    const calcWidth = calcMetricColumnWidth({
      title: activeRatioMetricName || '',
      hasSortIcon: true,
      hasRatioIcon: false,
      hasTipText: Boolean(tipsContent),
    });
    const width = activeRatioMetricColumnWidth ?? calcWidth;

    return {
      key: 'active_ratio',
      header: (
        <ColumnAligner align="right" width={width}>
          <div
            className={classnames('dbh-publisher-table-direct-sort', {
              'dbh-publisher-table-direct-sort--active': shareSortType !== SortType.None,
            })}
          >
            <Bubble
              style={{ maxWidth: 500 }}
              dark
              placement={isLastColumn ? 'top-end' : 'top'}
              arrowPointAtCenter={!isLastColumn}
              content={tipsContent}
            >
              <span
                style={{ cursor: !!tipsContent ? 'pointer' : 'normal' }}
                className="dbh-publisher-table-direct-sort-title"
                onClick={handleShareSort}
              >
                {activeRatioMetricName}
              </span>
            </Bubble>
            <div className="dbh-publisher-table-direct-sort-icon" onClick={handleShareSort}>
              <SortByIcon forceSortTypeNone={shareSortType === SortType.None} sortType={shareSortType} />
            </div>
          </div>
        </ColumnAligner>
      ),
      align: 'right' as const,
      width,
      render: (record: IRecord) => {
        const dauShare = record?.[dauShareMetricKey];
        return (
          <div className="dbh-publisher-data">
            <p className="dbh-publisher-data-value">{percentFormat(dauShare, 2, false, '--')}</p>
          </div>
        );
      },
    };
  };

  /**
   * 构建游戏行环比气泡数据（复用公共字段：粒度、日期、游戏图标/名称）
   */
  const buildGameDataCompareData = (
    record: IRecord,
    metricKey: string,
    fallbackTitle: string,
    valueFormatter: (val?: number | string) => string,
  ) => ({
    granularity: granularity as IGranularity,
    startDate,
    endDate,
    icon: record?.cover,
    title: record?.name || fallbackTitle,
    value: record?.[metricKey],
    prevValue: record?.[`${metricKey}_prev`],
    valueFormatter,
  });

  /**
   * 构建指标列
   */
  const buildMetricColumns = () => {
    const isMonthly = granularity === 'monthly';
    const dauTitle = isMonthly
      ? formatMessage({ id: 'dbh_studio_casual_col_monthly_avg_dau' })
      : formatMessage({ id: 'dbh_studio_casual_col_daily_dau' });
    const iapTitle = isMonthly
      ? formatMessage({ id: 'dbh_studio_casual_kpi_monthly_iap_revenue' })
      : formatMessage({ id: 'dbh_studio_casual_kpi_daily_iap_revenue' });
    const downloadTitle = isMonthly
      ? formatMessage({ id: 'dbh_studio_casual_col_monthly_download' })
      : formatMessage({ id: 'dbh_studio_casual_col_daily_download' });

    // 获取指标解释文案
    const getMetricTips = (key: string): string | undefined => {
      const i18nKey = tipsI18nKeys?.[key];
      if (i18nKey) {
        return formatMessage({ id: i18nKey });
      }
      return undefined;
    };
    const columns: TableColumn<IRecord>[] = [];
    const dauMetricTips = getMetricTips('daily_avg_dau');
    const dauWidth = calcMetricColumnWidth({
      title: dauTitle,
      dataSource,
      hasSortIcon: true,
      hasRatioIcon: getHasRatioIcon(dataSource, 'daily_avg_dau', sorts),
      hasTipText: Boolean(dauMetricTips),
    });

    // 活跃规模占比列
    columns.push(buildDauShareColumn());

    // DAU 列
    columns.push(
      CommonColumnBuilders.buildKMBMetricColumn<IRecord>({
        key: dauMetricKey,
        locale,
        align: 'right',
        // width: dauWidth,
        width: isMetricColumnAutoWidth ? 'auto' : dauWidth,
        dataSource,
        metric: 'daily_avg_dau',
        metricName: dauTitle,
        metricTips: dauMetricTips,
        sorts,
        onSortsChange,
        ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
        getValue: (record) => record?.[dauMetricKey],
        getRatioValue: (record) => record?.[dauRatioMetricKey],
        getDataCompareData: (record) =>
          buildGameDataCompareData(record, dauMetricKey, dauTitle, (val) => formatKMBValue(val as number)),
      }),
    );

    // IAP 收入列（IAA 品类不显示）
    if (hasRevenue) {
      const iapMetricTips = getMetricTips('iap_revenue');
      const iapWidth = calcMetricColumnWidth({
        title: iapTitle,
        dataSource,
        hasSortIcon: true,
        hasRatioIcon: getHasRatioIcon(dataSource, 'iap_revenue', sorts),
        hasTipText: Boolean(iapMetricTips),
      });
      columns.push(
        CommonColumnBuilders.buildKMBMetricColumn<IRecord>({
          key: iapMetricKey,
          locale,
          align: 'right',
          // width: iapWidth,
          width: isMetricColumnAutoWidth ? 'auto' : iapWidth,
          dataSource,
          metric: 'iap_revenue',
          metricName: iapTitle,
          metricTips: iapMetricTips,
          sorts,
          onSortsChange,
          ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
          getValue: (record) => record?.[iapMetricKey],
          getRatioValue: (record) => record?.[iapRatioMetricKey],
          getDataCompareData: (record) =>
            buildGameDataCompareData(record, iapMetricKey, iapTitle, (val) => `$${formatKMBValue(val as number)}`),
        }),
      );
    }

    // 下载列
    const downloadMetricTips = getMetricTips('download');
    const downloadWidth = calcMetricColumnWidth({
      title: downloadTitle,
      dataSource,
      hasSortIcon: true,
      hasRatioIcon: getHasRatioIcon(dataSource, 'download', sorts),
      hasTipText: Boolean(downloadMetricTips),
    });
    columns.push(
      CommonColumnBuilders.buildKMBMetricColumn<IRecord>({
        key: downloadMetricKey,
        locale,
        align: 'right',
        // width: downloadWidth,
        width: isMetricColumnAutoWidth ? 'auto' : downloadWidth,
        dataSource,
        metric: 'download',
        metricName: downloadTitle,
        metricTips: downloadMetricTips,
        sorts,
        onSortsChange,
        ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
        getValue: (record) => record?.[downloadMetricKey],
        getRatioValue: (record) => record?.[downloadRatioMetricKey],
        getDataCompareData: (record) =>
          buildGameDataCompareData(record, downloadMetricKey, downloadTitle, (val) => formatKMBValue(val as number)),
      }),
    );

    return columns;
  };

  /**
   * 表格列配置
   */
  const columns = useMemo(() => {
    const baseColumns: TableColumn<IRecord>[] = [];

    // 勾选列（仅当 showChecker 为 true 时显示）
    if (showChecker) {
      // 全选时的勾选项（前5个游戏）
      const top5GameIds = sortedData.slice(0, CHECKER_TOP_COUNT).map((item) => item?.game_id);
      // 判断是否全选（仅当前5个游戏被勾选时为 true）
      const handleGetIsAllChecked = (value?: string[], values?: string[] | null) => {
        if (!Array.isArray(values) || !Array.isArray(value)) return false;
        if (value.length !== values.length) return false;
        return values.every((item) => value.includes(item));
      };

      baseColumns.push(
        CommonColumnBuilders.buildCheckerColumn({
          fixed: 'left' as const,
          width: 36,
          getRecordCheckOptionValue: (record: IRecord) => record?.game_id,
          values: top5GameIds,
          value: checkedList,
          onChange: onCheckedListChange,
          maxCheckLimit: 10,
          getIsAllChecked: handleGetIsAllChecked,
        }),
      );
    }

    if (showSequence) {
      // 预构建排名查找表，避免 O(n²) 的 findIndex
      const rankMap = new Map<string, number>();
      sortedData.forEach((item, index) => {
        const gameId = item?.game_id;
        if (gameId && !rankMap.has(gameId)) {
          rankMap.set(gameId, index);
        }
      });

      // 奖牌序号列
      baseColumns.push(
        CommonColumnBuilders.buildMedalSequenceColumn({
          fixed: 'left' as const,
          align: 'center',
          width: sequenceColumnWidth,
          getRank: (record) => {
            return rankMap.get(record?.game_id) ?? 0;
          },
        }),
      );
    }

    // 游戏信息列
    baseColumns.push({
      key: 'game_info',
      header: formatMessage({ id: 'dbh_studio_casual_col_game_name' }),
      // width: 250,
      width: gameColumnWidth,
      fixed: 'left' as const,
      render: renderGameInfo,
    });

    // 指标列
    baseColumns.push(...buildMetricColumns());

    return baseColumns;
  }, [
    activeRatioMetricName,
    checkedList,
    dauMetricKey,
    dauRatioMetricKey,
    dauShareMetricKey,
    dataSource,
    downloadMetricKey,
    downloadRatioMetricKey,
    genreOptionsMap,
    granularity,
    startDate,
    endDate,
    hasRevenue,
    iapMetricKey,
    iapRatioMetricKey,
    isLastColumn,
    locale,
    onCheckedListChange,
    onSortsChange,
    showChecker,
    showPublisherInfo,
    sortedData,
    sorts,
    tipsI18nKeys,
  ]);

  if (loading) {
    return (
      <div style={style} className={classnames('dbh-publisher-table', className)}>
        <Loading style={{ height: 492 }} />
      </div>
    );
  }

  return (
    <div style={style} className={classnames('dbh-publisher-table', className)}>
      <ConfigProvider locale={teaLocale}>
        <DBTable
          className={classnames({
            'db-table--has-checker': showChecker,
            'db-table--has-sequence': showSequence,
            'db-table--has-check-sequence': showSequence && showChecker,
          })}
          bordered={bordered}
          useHeaderSticky={false}
          usePagination={sortedData?.length > PAGE_SIZE_OPTIONS[0]}
          loading={loading}
          recordKey={(record: IRecord) => record?.game_id || ''}
          pagingQuery={pagingQuery}
          onPagingChange={onPagingChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          columns={columns}
          records={sortedData}
          emptyText={formatMessage({ id: 'common_nodata' })}
        />
      </ConfigProvider>
    </div>
  );
};

export default PublisherTable;
